/**
 * Integrytas Group - User-Defined Column Engine (UDC)
 * UdcRegion — one-line drop-in that makes a screen a custom-field host.
 *
 * A screen normalizes itself by (1) registerUdcHost(pageCode, entity, label) at
 * module load, and (2) dropping this anywhere in its edit form:
 *
 *   <UdcRegion entity="csm_company" pageCode={PAGE_CODE} pkColumn="company_id" pk={row?.company_id} />
 *
 * It renders the fields an admin placed on THIS page for the entity, loads the
 * row's ext_attributes, and persists changes autonomously (debounced) — so the
 * host needs no form-state or save wiring. Renders nothing when no fields are
 * placed here, so it's safe to add unconditionally.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { asfWorkspaceCode } from '../../lib/asf';
import { useUdcFields } from '../../hooks/use_udc_fields';
import { UdcCustomFields } from './UdcCustomFields';

export interface UdcRegionProps {
  entity: string;
  pageCode: string;
  pkColumn: string;
  pk: number | string | null | undefined;
  workspaceCode?: string | null;
  disabled?: boolean;
  title?: string;
}

export function UdcRegion({ entity, pageCode, pkColumn, pk, workspaceCode, disabled, title }: UdcRegionProps) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('common.udc.title', 'Custom Fields');
  const ws = workspaceCode !== undefined ? (workspaceCode ?? '') : (asfWorkspaceCode() || '');
  const udc = useUdcFields(entity, { pageCode, workspaceCode: ws });
  const [ext, setExt] = useState<Record<string, unknown>>({});
  const [status, setStatus] = useState<'' | 'saving' | 'saved' | 'error'>('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // Load the row's shadow attributes once a row + placed fields exist.
  useEffect(() => {
    if (pk == null || udc.fields.length === 0) { setExt({}); return; }
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb.from(entity).select('ext_attributes').eq(pkColumn, pk).single();
      if (!cancelled) setExt(((data as { ext_attributes?: Record<string, unknown> } | null)?.ext_attributes) ?? {});
    })();
    return () => { cancelled = true; };
  }, [entity, pkColumn, pk, udc.fields.length]);

  const onChange = useCallback((next: Record<string, unknown>) => {
    setExt(next);
    if (pk == null) return;            // nothing to persist until the row exists
    setStatus('saving');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const { error } = await apiDb.from(entity).update({ ext_attributes: next }).eq(pkColumn, pk);
      setStatus(error ? 'error' : 'saved');
    }, 600);
  }, [entity, pkColumn, pk]);

  if (udc.fields.length === 0) return null;

  return (
    <div className="mt-4 border-t border-csm-border-soft pt-3">
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-csm-2xs font-semibold uppercase tracking-wide text-csm-watermark">{resolvedTitle}</h4>
        {status === 'saving' && <span className="text-csm-2xs text-csm-watermark">{t('common.udc.saving', 'saving…')}</span>}
        {status === 'saved'  && <span className="text-csm-2xs text-csm-success">{t('common.udc.saved', 'saved')}</span>}
        {status === 'error'  && <span className="text-csm-2xs text-csm-danger">{t('common.udc.save_failed', 'save failed')}</span>}
        {pk == null && <span className="text-csm-2xs text-csm-watermark">{t('common.udc.save_first', '— save the record first, then set custom fields')}</span>}
      </div>
      <UdcCustomFields entity={entity} pageCode={pageCode} workspaceCode={ws} fields={udc.fields}
        value={ext} onChange={onChange} disabled={disabled || pk == null} />
    </div>
  );
}
